import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation,useParams } from 'react-router-dom';
import ClientHeaderNavigation from '../../components/ui/ClientHeaderNavigation';
import BookingProgressNavigation from '../../components/ui/BookingProgressNavigation';
import TimeSlotCard from './components/TimeSlotCard';
import BookingSummaryPanel from './components/BookingSummaryPanel';
import TimeSlotSkeleton from './components/TimeSlotSkeleton';
import EmptyTimeSlots from './components/EmptyTimeSlots';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import repositoryContainer from '../../services/RepositoryContainer';
import { useService } from '../../components/context/ServiceContext';
import { useBooking } from '../../components/context/BookingContext';
import { useCompany } from '../../components/context/CompanyContext';

const TimeSlotSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [timeSlots, setTimeSlots] = useState([]);
    const { company } = useCompany();
  const { companyPath } = useParams();

  // const [selectedDate, setSelectedDate] = useState(null);
  const { selectedServices} = useService();
const { 
  selectedDate, 
  selectedDateSlotId, 
  selectedTimeSlot, 
  selectedTimeSlotId, 
  setSelectedDate, 
  setSelectedTimeSlot, 
  resetBooking 
} = useBooking();

  // When the selected date changes, clear any previously selected time slot.
  // This prevents keeping a time selection that belongs to a different date.
  useEffect(() => {
    setSelectedTimeSlot(null);
  }, [selectedDate]);
  
  // No fallback mock data - show empty state instead

  const service = selectedServices?.[0];

  const formatDateForApi = (date) => {
    if (!date) return null;

    // Accept Date objects or strings.
    const parsed = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsed?.getTime?.())) return null;

    return parsed.toISOString().split('T')[0];
  };

  const fetchTimeSlots = async (date) => {
    const apiDate = formatDateForApi(date);
    if (!apiDate) return null;

    try {
      const slots = await repositoryContainer.dateSlot.getClientTimeSlots(apiDate, company?.id);
      return slots;
    } catch (error) {
      console.error('Failed to fetch time slots:', error);
      return null;
    }
  };

  const loadTimeSlots = async (date) => {
    setIsLoading(true);
    try {
      const data = await fetchTimeSlots(date);
      if (Array.isArray(data)) {
        setTimeSlots(data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      loadTimeSlots(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    const dateFromState = location?.state?.selectedDate;
    const dateSlotIdFromState = location?.state?.selectedDateSlotId;
    const timeSlotFromState = location?.state?.selectedTimeSlot;
    const timeSlotIdFromState = location?.state?.selectedTimeSlotId;

    if (dateFromState) {
      setSelectedDate(dateFromState, dateSlotIdFromState);
    }

    if (timeSlotFromState) {
      setSelectedTimeSlot(timeSlotFromState, timeSlotIdFromState);
    }

    // when coming back to this page we may already have a date
    const dateToLoad = dateFromState || selectedDate;
    if (dateToLoad) {
      loadTimeSlots(dateToLoad);
    } else {
      setIsLoading(false);
    }
  }, [location?.state]);

  const handleSlotSelect = (slot) => {
    setSelectedTimeSlot(slot);
  };

  const handleContinue = () => {
    if (selectedTimeSlot) {
      navigate(`/${companyPath}/client-details-form`, {
        state: {
          selectedDate,
          selectedDateSlotId,
          selectedTimeSlot,
          selectedTimeSlotId,
          service
        }
      });
    }
  };

  const handleBack = () => {
    navigate(`/${companyPath}/date-selection`);
  };

  const handleRestartBooking = () => {
    resetBooking();
    navigate(`/${companyPath}/date-selection`);
  };

  const handleSelectDifferentDate = () => {
    resetBooking();
    navigate(`/${companyPath}/date-selection`);
  };

  const availableSlots = timeSlots?.filter(slot => !slot?.isBlocked) || [];

  return (
    <div className="min-h-screen bg-background">
      <ClientHeaderNavigation onRestartBooking={handleRestartBooking} />
      <BookingProgressNavigation
        currentStep={2}
        totalSteps={3}
        canGoBack={true}
        canGoForward={!!selectedTimeSlot}
        onBack={handleBack}
        onForward={handleContinue}
        steps={[
          { label: 'Select Date', path: `/${companyPath}/date-selection` },
          { label: 'Choose Time', path: `/${companyPath}/time-slot-selection` },
          { label: 'Your Details', path: `/${companyPath}/client-details-form` }
        ]}
      />
      <main className="pt-5 md:pt-5 lg:pt-5 pb-8 md:pb-12 lg:pb-16 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6 md:mb-8 lg:mb-10">
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-2 md:mb-3">
              Choose Your Time
            </h1>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground font-caption">
              Select your preferred appointment time from available slots
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
            <div className="lg:col-span-2">
              <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-sm border border-border">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="font-heading font-semibold text-xl md:text-2xl lg:text-3xl text-foreground">
                    Available Time Slots
                  </h2>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-success/10 rounded-full">
                    <Icon name="Check" size={16} color="var(--color-success)" />
                    <span className="text-xs md:text-sm font-caption text-success font-medium">
                      {availableSlots?.length} Available
                    </span>
                  </div>
                </div>

                {isLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-5">
                    {[...Array(9)]?.map((_, index) => (
                      <TimeSlotSkeleton key={index} />
                    ))}
                  </div>
                ) : timeSlots?.length === 0 ? (
                  <EmptyTimeSlots onSelectDifferentDate={handleSelectDifferentDate} />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-5">
                    {timeSlots?.map((slot) => (
                      <TimeSlotCard
                        key={slot?.id}
                        slot={slot}
                        isSelected={selectedTimeSlot?.id === slot?.id}
                        onSelect={handleSlotSelect}
                      />
                    ))}
                  </div>
                )}

                <div className="mt-6 md:mt-8 p-4 md:p-5 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-caption font-medium text-sm md:text-base text-foreground mb-1">
                        Booking Information
                      </h4>
                      <p className="text-xs md:text-sm text-muted-foreground font-caption">
                        {company?.clientProtocols?.arrivalPolicy || 'All appointments include a complimentary consultation.' }. {company?.clientProtocols?.cancellationPolicy || 'Please arrive 5 minutes early for your scheduled time. Cancellations must be made at least 24 hours in advance.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-32 md:top-36 lg:top-40">
                <BookingSummaryPanel
                  selectedDate={selectedDate}
                  selectedSlot={selectedTimeSlot}
                  service={service}
                />

                <div className="hidden lg:block mt-0 md:mt-6">
                  <Button
                    variant="default"
                    fullWidth
                    iconName="ArrowRight"
                    iconPosition="right"
                    disabled={!selectedTimeSlot}
                    onClick={handleContinue}
                  >
                    Continue to Details
                  </Button>
                </div>

                <div className="mb-12 mt-2 md:mt-4 lg:mb-0">
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={handleSelectDifferentDate}
                  >
                    Change Date
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {selectedTimeSlot && (
        <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-card border-t border-border p-4 shadow-lg z-40">
          <Button
            variant="default"
            fullWidth
            iconName="ArrowRight"
            iconPosition="right"
            onClick={handleContinue}
          >
            Continue to Details
          </Button>
        </div>
      )}
    </div>
  );
};

export default TimeSlotSelection;